import { z } from "zod";
import { ZAccess, ZAccessPopulated, ZMongoIdStr, ZObjectIdString, ZUserPartial } from "./accessControlSchema";
export { ZAccess, ZAccessLevel, ZAccessPopulated, ZGroupEnum, ZMongoIdStr, ZObjectIdString, ZUserPartial, } from "./accessControlSchema";
export const ZDateTimeString = z.preprocess((value) => (value instanceof Date ? value.toISOString() : value), z.string().datetime({ offset: true }));
export const ZDateTime = z.union([ZDateTimeString, z.date()]);
export const ZValueAndLabel = z.object({
    value: z.string(),
    label: z.string(),
});
export const ZValueAndLabelOptionalActive = ZValueAndLabel.extend({
    isActive: z.boolean(),
});
export const ZAllFilter = z.object({
    dataField: z.string(),
    options: z.array(ZValueAndLabelOptionalActive).optional(),
    bool: z.boolean().optional(),
});
export const ZStoredIssueColConfigs = z.object({
    colId: z.string().optional(),
    orderPriority: z.number().optional(),
    width: z.number().optional(),
});
export const ZSortColumn = z.object({
    column: z.string().optional(),
    direction: z.enum(["ASC", "DESC"]).optional(),
});
export const ZTableTemplateFilter = z.object({
    typeOfFilter: z.enum(["MULTIFILTER", "BOOLEANFILTER"]).optional(),
    dataField: z.string().optional(),
    multiFilterValues: z.union([z.array(z.string()), z.array(z.object({}))]).optional(),
    isActive: z.boolean().optional(),
});
export const ZTaskAndOrIssue = z
    .array(z.enum(["TASK", "ISSUE"]))
    .default(["ISSUE"])
    .optional();
export const ZTableTemplate = z.object({
    _id: ZObjectIdString,
    name: z.string(),
    issueBoard: ZObjectIdString,
    tableColumns: z.array(z.string().optional()),
    tableColConfigs: z.array(ZStoredIssueColConfigs).default([]).optional(),
    taskAndOrIssue: ZTaskAndOrIssue,
    filters: z.array(ZTableTemplateFilter),
    sortedCol: ZSortColumn.optional(),
    archived: z.boolean().default(false).optional(),
});
export const ZIssueStatusEnum = z.enum(["Fullført", "I prosess", "På hold", "Opprettet", "Utgår"]);
export const ZOtherAttributesTypeOfValueEnums = z.enum([
    "STRING",
    "MULTILINESTRING",
    "NUMBER",
    "DATE",
    "BOOLEAN",
    "DROPDOWN",
    "FORMULA",
    "MATH_FORMULA",
]);
export const ZDropdownOptionValueTypeEnums = z.enum(["STRING", "NUMBER"]);
const ZDropdownDefaultValue = z.union([z.string(), z.number()]);
export const ZOtherAttributesFormulaConfItem = z.object({
    id: z.string().optional(),
    type: z.enum(["DELIMITER", "ATTRIBUTE", "COUNTER"]),
    value: z.string(),
});
export const ZOtherAttributesFormula = z.object({
    nrOfCharsInCounter: z.number().default(3).optional(),
    deletePreviousDelimiterIfAttributeIsEmpty: z.boolean().optional(),
    counterMode: z.enum(["AUTO", "MANUAL"]).default("AUTO").optional(),
    manualCounter: z
        .object({
        enforceUniqueWithinGroup: z.boolean().default(true).optional(),
    })
        .optional(),
    confArr: z.array(ZOtherAttributesFormulaConfItem),
});
export const ZOtherAttributesMathFormula = z.object({
    expression: z.string().optional(),
    variables: z.array(z.string()).optional(),
});
export const ZOtherAttributeConfigItemProps = z
    .object({
    typeOfValue: ZOtherAttributesTypeOfValueEnums,
    dropdownOptionType: ZDropdownOptionValueTypeEnums.optional(),
    tableOptions: z
        .array(z.object({
        _id: ZObjectIdString.optional(),
        value: z.union([z.string(), z.number()]),
        description: z.string().nullable(),
        color: z.string().optional(),
    }))
        .max(100)
        .optional(),
    defaultDropdownValue: ZDropdownDefaultValue.nullable().optional(),
    defaultTextValue: z.string().nullable().optional(),
    defaultNumberValue: z.number().nullable().optional(),
    allowMultipleSelectionsInDropdown: z.boolean().optional(),
    label: z.string(),
    labelColor: z.string().optional(),
    section: z.string(),
    description: z.string(),
    order: z.number(),
    isActive: z.boolean(),
    formula: ZOtherAttributesFormula.optional(),
    mathFormula: ZOtherAttributesMathFormula.optional(),
    locked: z.boolean().optional(),
})
    .superRefine((value, ctx) => {
    if (value.typeOfValue === "DROPDOWN" && value.dropdownOptionType === "NUMBER") {
        value.tableOptions?.forEach((option, index) => {
            if (typeof option.value !== "number" || Number.isNaN(option.value)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["tableOptions", index, "value"],
                    message: "Dropdown options must be numbers when dropdownOptionType is NUMBER",
                });
            }
        });
    }
    if (value.typeOfValue === "DROPDOWN" && (!value.dropdownOptionType || value.dropdownOptionType === "STRING")) {
        value.tableOptions?.forEach((option, index) => {
            if (typeof option.value !== "string") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["tableOptions", index, "value"],
                    message: "Dropdown options must be text when dropdownOptionType is STRING",
                });
            }
        });
    }
    if (value.defaultDropdownValue != null && value.typeOfValue === "DROPDOWN") {
        if (value.allowMultipleSelectionsInDropdown === true) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaultDropdownValue"],
                message: "Default dropdown value is only supported for single-selection dropdowns",
            });
        }
        if (value.dropdownOptionType === "NUMBER" && typeof value.defaultDropdownValue !== "number") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaultDropdownValue"],
                message: "Default dropdown value must be a number when dropdownOptionType is NUMBER",
            });
        }
        if ((!value.dropdownOptionType || value.dropdownOptionType === "STRING") &&
            typeof value.defaultDropdownValue !== "string") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaultDropdownValue"],
                message: "Default dropdown value must be text when dropdownOptionType is STRING",
            });
        }
        const isOptionValue = value.tableOptions?.some((option) => option.value === value.defaultDropdownValue);
        if (isOptionValue === false) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaultDropdownValue"],
                message: "Default dropdown value must be one of the dropdown options",
            });
        }
    }
    if (value.defaultTextValue != null && !["STRING", "MULTILINESTRING"].includes(value.typeOfValue)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["defaultTextValue"],
            message: "Default text value is only supported for text attributes",
        });
    }
    if (value.defaultNumberValue != null && value.typeOfValue !== "NUMBER") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["defaultNumberValue"],
            message: "Default number value is only supported for number attributes",
        });
    }
    if (value.typeOfValue === "MATH_FORMULA" &&
        (!value.mathFormula?.expression || value.mathFormula.expression.trim() === "")) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["mathFormula", "expression"],
            message: "Required",
        });
    }
});
export const ZCapitalLetters = z.string().regex(/^[A-Z]+$/);
export const ZOtherAttributeConfigItemKeyEnum = z.enum(["typeOfValue", "label", "section", "order", "isActive"]);
export const ZOtherAttributesAddPostBody = z.object({
    issueBoardId: ZObjectIdString,
    otherAttributesConfigItemProps: ZOtherAttributeConfigItemProps,
    taskOrIssue: z.enum(["TASK", "ISSUE"]),
});
export const ZOtherAttributesUpdateUpdateBody = z.object({
    issueBoardId: ZObjectIdString,
    otherAttributeConfigItemProps: ZOtherAttributeConfigItemProps,
    otherAttributeConfigKey: ZCapitalLetters,
    taskOrIssue: z.enum(["TASK", "ISSUE"]),
});
export const ZOtherAttributeConfigApi = z.record(ZCapitalLetters, ZOtherAttributeConfigItemProps);
export const ZOtherAttributeConfigDb = z.map(ZCapitalLetters, ZOtherAttributeConfigItemProps);
export const ZIssueOtherAttributesUpdateOrderBody = z.object({
    issueBoardId: ZObjectIdString,
    idsInOrder: z.array(z.string()),
    taskOrIssue: z.enum(["TASK", "ISSUE"]),
});
/// Predefined tags section
export const ZPredefinedTag = z.object({
    _id: ZObjectIdString.optional(),
    title: z.string(),
});
export const ZUpdateIssueBoardPredefinedTags = z.object({
    projectId: ZMongoIdStr,
    issueBoardId: ZMongoIdStr,
    tags: z.array(ZPredefinedTag),
});
export const ZUpdateIssueBoardUsePredefinedTags = z.object({
    projectId: ZMongoIdStr,
    issueBoardId: ZMongoIdStr,
    useCustomTags: z.boolean(),
});
export const ZUpdateIssueBoardUseOnlyPredefinedTags = z.object({
    projectId: ZMongoIdStr,
    issueBoardId: ZMongoIdStr,
    useOnlyPredefinedTags: z.boolean(),
});
/// Risiko section
export const ZUpdateIsActive = z.object({
    projectId: ZMongoIdStr,
    issueBoardId: ZMongoIdStr,
    isActive: z.boolean(),
});
export const ZRisikoProbability = z.object({
    // id can be null or string
    id: z.string().optional(),
    name: z.string(),
    description: z.string(),
    matrixValue: z.number(),
    isActive: z.boolean(),
    order: z.number(),
});
export const ZUpdateRisikoProbability = z.object({
    projectId: ZMongoIdStr,
    issueBoardId: ZMongoIdStr,
    probabilities: z.array(ZRisikoProbability).refine((probabilities) => {
        const orders = probabilities.map((prob) => prob.order);
        return new Set(orders).size === orders.length;
    }, {
        message: "All 'order' values must be unique",
    }),
});
export const ZProjectBoardIds = z.object({
    projectId: ZMongoIdStr,
    issueBoardId: ZMongoIdStr,
});
export const ZGridProbabilityWithId = ZRisikoProbability.extend({
    id: z.string(),
    isActive: z.enum(["Ja", "Nei"]),
});
export const ZConsequence = z.object({
    id: z.string().optional(),
    name: z.string(),
    description: z.string().nullable(),
    matrixValue: z.number(),
    isActive: z.boolean(),
    order: z.number(),
});
export const ZConsequenceWithId = ZConsequence.extend({
    id: z.string(),
});
export const ZConsequenceArray = z.array(ZConsequenceWithId).refine((consequences) => {
    const orders = consequences.map((consequence) => consequence.order);
    return new Set(orders).size === orders.length;
}, {
    message: "All 'order' values must be unique",
});
export const ZGridConsequenceWithId = ZConsequenceWithId.extend({
    isActive: z.enum(["Ja", "Nei"]),
});
export const ZMatrixItem = z.object({ color: z.string() });
export const ZMatrixConfig = z.array(z.array(ZMatrixItem));
const ZConsequenceRecord = z.record(z.string(), ZConsequence);
const ZConsequenceRecordDb = z.map(z.string(), ZConsequence);
const ZEvaluationCategoryBase = z.object({
    name: z.string(),
    description: z.string(),
    isActive: z.boolean(),
    type: z.enum(["RISK", "OPPORTUNITY"]),
    matrixConfig: ZMatrixConfig,
});
export const ZEvaluationCategoryApi = ZEvaluationCategoryBase.extend({
    consequences: ZConsequenceRecord,
});
export const ZEvaluationCategoryDb = ZEvaluationCategoryBase.extend({
    consequences: ZConsequenceRecordDb,
});
const ZMapOfEvaluationCategoriesDb = z.map(z.string(), ZEvaluationCategoryDb);
const ZMapOfEvaluationCategoriesApi = z.record(z.string(), ZEvaluationCategoryApi);
const ZMapOfRisikoProbabilitiesDb = z.map(z.string(), ZRisikoProbability);
const ZMapOfRisikoProbabilitiesApi = z.record(z.string(), ZRisikoProbability);
const ZRisikoSectionBase = z.object({
    isActive: z.boolean(),
    enableProbabilityAfterTiltak: z.boolean(),
});
export const ZRisikoSectionDb = ZRisikoSectionBase.extend({
    probabilites: ZMapOfRisikoProbabilitiesDb,
    evaluationCategoriesConfig: ZMapOfEvaluationCategoriesDb,
});
export const ZRisikoSectionApi = ZRisikoSectionBase.extend({
    probabilites: ZMapOfRisikoProbabilitiesApi,
    evaluationCategoriesConfig: ZMapOfEvaluationCategoriesApi,
});
export const ZUpdateEvaluation = z.object({
    projectId: ZMongoIdStr,
    issueBoardId: ZMongoIdStr,
    prop: z.string(),
    id: z.string(),
    newValue: z.union([z.string(), z.boolean(), ZConsequenceArray, ZMatrixConfig]),
});
export const ZMoveIssueBoardToOtherProject = z.object({
    maconomyProjectNo: z.number().min(100),
    issueBoardId: ZMongoIdStr,
});
export const ZAddRisikoEvalCategory = ZProjectBoardIds.extend({
    name: z.string(),
});
export const ZNoteboardImageNode = z.object({
    _id: ZObjectIdString.optional(),
    path: z.string(),
    x: z.number(),
    y: z.number(),
    height: z.number(),
    width: z.number(),
});
export const ZAddOrUpdateNoteboardImageNode = z.object({
    projectId: ZMongoIdStr,
    issueBoardId: ZMongoIdStr,
    noteboardImageNode: ZNoteboardImageNode,
});
export const ZDeleteNoteboardImageNode = z.object({
    projectId: ZMongoIdStr,
    issueBoardId: ZMongoIdStr,
    noteboardImageNodeId: ZObjectIdString.optional(),
});
export const ZProgressData = z.object({
    percentage: z.number(),
    color: z.string(),
});
export const ZMadeFromTemplateInfo = z.object({
    title: z.string(),
    id: ZObjectIdString.optional(),
});
// Hjälpschema för en historikrad
export const ZTemplateHistoryEntry = z.object({
    action: z.enum([
        "IKKE AKTIV",
        "AKTIV",
        "CREATED",
        "SET_TO_REVIEW",
        "UNDER_REVIEW",
        "APPROVED",
        "REJECTED",
        "UPDATED",
    ]),
    by: ZUserPartial,
    date: z.date().or(z.string().datetime()), // Accepterar både Date-objekt och ISO-strängar
    comment: z.string().optional().default(""),
});
export const ZBoardTemplateStatus = z.enum(["IKKE AKTIV", "AKTIV", "CREATED", "UNDER_REVIEW", "APPROVED"]);
export const ZBoardTemplateInfo = z.object({
    // Befintliga fält (oförändrade)
    isTemplate: z.boolean().default(false),
    description: z.string().default(""),
    // Admin – nu med ZUserPartial istället för z.any()
    admin: ZUserPartial.optional(),
    // ────────────────────────────────────────────────
    // NYA FÄLT FÖR GODKÄNNANDEPROCESS
    // ────────────────────────────────────────────────
    // Status – bara relevant om isTemplate = true
    status: ZBoardTemplateStatus.optional(),
    // Vem som godkände (sparar namn + id direkt)
    approvedBy: ZUserPartial.optional(),
    // När det godkändes
    approvalDate: z.date().or(z.string().datetime()).optional(),
    // Historik över statusändringar och viktiga händelser
    history: z.array(ZTemplateHistoryEntry).optional(),
});
export const ZBoardType = z.object({
    _id: ZObjectIdString.optional(),
    name: z.string(),
});
export const ZBoardBucket = z.object({
    _id: ZObjectIdString.optional(),
    value: z.string(),
    order: z.number(),
    status: ZIssueStatusEnum.optional(),
});
export const ZIssueViewEnum = z.enum([
    "TABLE",
    "GIS",
    "KANBAN",
    "NOTEBOARD",
    "DASHBOARD",
    "PNS",
    "GANTT",
    "BIM",
    "NONE",
]);
export const ZIssueBoardStatus = z.enum(["ACTIVE", "IN_DEVELOPMENT", "DELIVERED", "DELETED", "INACTIVE", "COMPLETED"]);
export const ZGanttVacationSetting = z.object({
    startDate: ZDateTime,
    endDate: ZDateTime,
    title: z.string(),
});
const ZIssueBoardBase = z.object({
    _id: ZObjectIdString,
    createdAt: ZDateTime,
    project: ZObjectIdString,
    name: z.string(),
    access: z.optional(ZAccess),
    status: ZIssueBoardStatus,
    lastUpdatedBy: z.optional(ZUserPartial),
    createdFromTemplate: z.optional(z.string()),
    progress: z.optional(z.array(ZProgressData)),
    pns: z.optional(z.string()),
    createdDate: z.optional(ZDateTime),
    disabledColumns: z.optional(z.array(z.string())),
    disabledColumnsTasks: z.optional(z.array(z.string())),
    tableTemplates: z.optional(z.array(ZTableTemplate)),
    buckets: z.optional(z.array(ZBoardBucket)),
    types: z.optional(z.array(ZBoardType)),
    useBucketsAsWorkflow: z.optional(z.boolean()),
    allFilters: z.optional(z.array(ZAllFilter)),
    showConclusion: z.optional(z.boolean()),
    template: z.optional(ZBoardTemplateInfo),
    defaultView: z.optional(ZIssueViewEnum),
    defaultTemplate: z.optional(ZObjectIdString),
    issueNameSetting: z.optional(z.string()),
    taskNameSetting: z.optional(z.string()),
    dashboardSetting: z.optional(z.string()),
    category: z.optional(z.string().nullable()),
    description: z.optional(z.string()),
    bucketNameSetting: z.optional(z.string()),
    showCompletedTodosInKanban: z.optional(z.boolean()),
    completedIssuesFetchLimitMonths: z.optional(z.number().nullable()),
    usePredefinedTags: z.optional(z.boolean()),
    useOnlyPredefinedTags: z.optional(z.boolean()),
    predefinedTags: z.optional(z.array(ZPredefinedTag)),
    madeFromTemplate: z.optional(ZMadeFromTemplateInfo),
    noteboardImageNodes: z.optional(z.array(ZNoteboardImageNode)),
    createdBy: z.optional(z.union([ZUserPartial, ZObjectIdString])),
    otherAttributesTasksConfig: z.optional(ZOtherAttributeConfigApi),
    otherAttributesConfig: z.optional(ZOtherAttributeConfigApi),
    risikoSection: z.optional(ZRisikoSectionApi),
    ganttVacationSettings: z.optional(z.array(ZGanttVacationSetting)),
    disableDeleteIssue: z.optional(z.boolean()),
});
export const ZIssueBoardDBSchema = ZIssueBoardBase.extend({
    createdBy: z.optional(ZObjectIdString),
    otherAttributesTasksConfig: z.optional(ZOtherAttributeConfigDb),
    otherAttributesConfig: z.optional(ZOtherAttributeConfigDb),
    risikoSection: z.optional(ZRisikoSectionDb),
});
export const ZIssueBoardApi = ZIssueBoardBase.extend({
    createdBy: z.optional(ZUserPartial),
    access: ZAccessPopulated,
    otherAttributesTasksConfig: z.optional(ZOtherAttributeConfigApi),
    otherAttributesConfig: z.optional(ZOtherAttributeConfigApi),
    risikoSection: z.optional(ZRisikoSectionApi),
});
export const ZIssueBoardSchema = ZIssueBoardBase;
